import { Component } from '@theme/component';

const GREETING_DISMISSED_KEY = 'whatsapp-button-greeting-dismissed-at';
const GREETING_FREQUENCY_DAYS = 1;

/**
 * Sends a GA4 event via gtag if it's present on the page. No-ops otherwise.
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 */
function trackEvent(name, params = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

/**
 * Site-wide floating WhatsApp button: opens a wa.me chat with a pre-filled
 * message, shows a business-hours-aware status, and surfaces an auto-appearing
 * greeting bubble that doesn't nag repeat visitors.
 */
class WhatsappButton extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);

    this.#applyBusinessHoursStatus();
    this.#scheduleGreeting();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.clearTimeout(this.timeoutId);
  }

  get #isOnline() {
    if (this.dataset.hoursEnabled !== 'true') return true;

    const now = new Date();
    const day = now.getDay(); // 0 = Sun ... 6 = Sat
    const hour = now.getHours();

    if (day === 0) return false; // Sunday: always closed

    if (day === 6) {
      if (this.dataset.saturdayEnabled !== 'true') return false;
      const start = Number(this.dataset.saturdayStart ?? 8);
      const end = Number(this.dataset.saturdayEnd ?? 13);
      return hour >= start && hour < end;
    }

    const start = Number(this.dataset.weekdayStart ?? 8);
    const end = Number(this.dataset.weekdayEnd ?? 17);
    return hour >= start && hour < end;
  }

  #applyBusinessHoursStatus() {
    const online = this.#isOnline;
    const dot = this.querySelector('[data-whatsapp-dot]');
    const statusText = this.querySelector('[data-whatsapp-status]');

    if (this.dataset.hoursEnabled !== 'true') {
      dot?.setAttribute('hidden', '');
      if (statusText) statusText.textContent = '';
      return;
    }

    dot?.toggleAttribute('hidden', !online);

    if (statusText) {
      statusText.dataset.online = String(online);
      statusText.textContent =
        statusText.dataset[online ? 'onlineText' : 'offlineText'] || '';
    }
  }

  #scheduleGreeting() {
    if (this.dataset.greetingEnabled !== 'true') return;

    const dismissedAt = Number(localStorage.getItem(GREETING_DISMISSED_KEY) || 0);
    if (dismissedAt) {
      const daysSinceDismiss = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < GREETING_FREQUENCY_DAYS) return;
    }

    const delaySeconds = Number(this.dataset.greetingDelay || 8);
    this.timeoutId = window.setTimeout(() => this.#showGreeting(), delaySeconds * 1000);
  }

  #showGreeting() {
    const bubble = this.querySelector('[data-whatsapp-bubble]');
    if (!bubble) return;
    bubble.removeAttribute('hidden');
    requestAnimationFrame(() => bubble.classList.add('whatsapp-button__bubble--visible'));
    trackEvent('whatsapp_greeting_view');
  }

  #dismissGreeting() {
    const bubble = this.querySelector('[data-whatsapp-bubble]');
    bubble?.classList.remove('whatsapp-button__bubble--visible');
    localStorage.setItem(GREETING_DISMISSED_KEY, String(Date.now()));
  }

  #openChat() {
    const phone = this.dataset.phone;
    if (!phone) return;

    const message = this.dataset.message || '';
    const url = `https://wa.me/${phone}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
    window.open(url, '_blank', 'noopener');
    trackEvent('whatsapp_button_click');
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-whatsapp-bubble-close]')) {
      this.#dismissGreeting();
    } else if (target.closest('[data-whatsapp-open]') || target.closest('[data-whatsapp-bubble]')) {
      this.#openChat();
    }
  };
}

if (!customElements.get('whatsapp-button')) {
  customElements.define('whatsapp-button', WhatsappButton);
}
